/**
 * src/hooks/useSupport.js
 *
 * React hook for support ticket state management.
 * Uses Firestore real-time listeners for the current user's tickets.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import supportService from "../services/supportService";
import { useAuth } from "../hooks/useAuth";

/**
 * Listen to the current user's support tickets in real time.
 * @param {{ status?: string }} opts
 * @returns {[Array, boolean, string|null]}
 */
export function useTickets(opts = {}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const mounted = useRef(true);
  const unsubRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    setLoading(true);

    if (!user) {
      setTickets([]);
      setLoading(false);
      return;
    }

    unsubRef.current = supportService.listenUserTickets(
      user,
      (data) => {
        if (mounted.current) {
          setTickets(data);
          setLoading(false);
          setError(null);
        }
      },
      opts.status || null
    );

    return () => {
      mounted.current = false;
      if (unsubRef.current) unsubRef.current();
    };
  }, [user, opts.status]);

  return [tickets, loading, error];
}
